import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Package,
  CreditCard,
  Calendar,
  Users,
  Bed,
  FileText,
  Pencil,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';

const GET_BOOKING = gql`
  query GetBooking($id: Int!) {
    booking(id: $id) {
      id
      status
      statutReservation
      travelerCount
      totalPrice
      contactInfo
      specialRequests
      package { id title }
      formula { id name }
      client { id firstName lastName email phone }
      nbrAdulte
      nbrEnfant
      nbrBebe
      nbrChambre
      nom
      prenom
      telephone
      mail
      typeReservation
      personnes
      bebes
      chambres
      paiement
      montantAvance
      montantPaye
      statutDisponibilite
      delaiOption
      createdAt
      updatedAt
    }
  }
`;

function formatBookingDate(value: string | number | Date | null | undefined): string {
  if (value == null || value === '') return '—';
  try {
    let date: Date;
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'number') {
      date = value < 1e12 ? new Date(value * 1000) : new Date(value);
    } else {
      const str = String(value).trim();
      if (!str) return '—';
      // Timestamp en chaîne (ex. "1772029161055" envoyé par le backend)
      const num = /^\d+$/.test(str) ? parseInt(str, 10) : NaN;
      if (!Number.isNaN(num)) {
        date = num < 1e12 ? new Date(num * 1000) : new Date(num);
      } else {
        date = new Date(str.replace(' ', 'T'));
      }
    }
    if (isNaN(date.getTime())) return '—';
    return format(date, "dd MMMM yyyy 'à' HH:mm", { locale: fr });
  } catch {
    return '—';
  }
}

function PaymentBadge({ paiement }: { paiement?: string | null }) {
  const pay = paiement || 'non_paye';
  let labelPay = 'Non payé';
  let classPay = 'bg-amber-100 text-amber-800';
  if (pay === 'paye') {
    labelPay = 'Payé';
    classPay = 'bg-emerald-100 text-emerald-800';
  } else if (pay === 'avance') {
    labelPay = 'Acompte';
    classPay = 'bg-blue-100 text-blue-800';
  }
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${classPay}`}>
      {labelPay}
    </span>
  );
}

export default function BookingRecapPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(GET_BOOKING, {
    variables: { id: bookingId ? parseInt(bookingId) : 0 },
    skip: !bookingId || isNaN(Number(bookingId)),
  });
  const booking = data?.booking;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="text-gray-500">Chargement de la réservation...</div>
        </div>
      </AdminLayout>
    );
  }
  if (error) {
    return (
      <AdminLayout>
        <div className="p-6 text-center text-red-600">
          Erreur lors du chargement de la réservation.
        </div>
      </AdminLayout>
    );
  }
  if (!booking) {
    return (
      <AdminLayout>
        <div className="p-6 text-center text-gray-600">Réservation introuvable.</div>
      </AdminLayout>
    );
  }

  const clientName =
    booking.nom && booking.prenom
      ? `${booking.nom} ${booking.prenom}`
      : booking.client
        ? `${booking.client.firstName || ''} ${booking.client.lastName || ''}`.trim()
        : '—';
  const clientEmail = booking.mail || booking.client?.email || '—';
  const clientPhone = booking.telephone || booking.client?.phone || '—';

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(booking.package?.id ? `/admin/packages/${booking.package.id}/clients` : '/admin/bookings')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la liste
          </button>
          <button
            type="button"
            onClick={() => navigate(`/admin/bookings/edit/${booking.id}`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
          >
            <Pencil className="w-4 h-4" />
            Modifier
          </button>
        </div>

        {/* En-tête */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Récapitulatif — Réservation #{booking.id}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Créée le {formatBookingDate(booking.createdAt)}
                </p>
              </div>
              <PaymentBadge paiement={booking.paiement} />
            </div>
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          {/* Package & formule */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Séjour</h2>
            </div>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Package</dt>
                <dd className="text-gray-900 font-medium">{booking.package?.title || '—'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Formule</dt>
                <dd className="text-gray-900">{booking.formula?.name || '—'}</dd>
              </div>
            </dl>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Contact</h2>
            </div>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Nom du client</dt>
                <dd className="text-gray-900 font-medium">{clientName}</dd>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <dt className="text-sm text-gray-500">Email</dt>
                  <dd className="text-gray-900 break-all">{clientEmail}</dd>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <dt className="text-sm text-gray-500">Téléphone</dt>
                  <dd className="text-gray-900">{clientPhone}</dd>
                </div>
              </div>
            </dl>
          </div>

          {/* Voyageurs & chambres */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Voyageurs & chambres</h2>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Effectif</h3>
                <ul className="space-y-1 text-gray-900">
                  <li>{booking.nbrAdulte ?? 0} adulte{(booking.nbrAdulte ?? 0) !== 1 ? 's' : ''}</li>
                  {(booking.nbrEnfant ?? 0) > 0 && (
                    <li>{booking.nbrEnfant} enfant{(booking.nbrEnfant ?? 0) !== 1 ? 's' : ''}</li>
                  )}
                  {(booking.nbrBebe ?? 0) > 0 && (
                    <li>{booking.nbrBebe} bébé{(booking.nbrBebe ?? 0) !== 1 ? 's' : ''}</li>
                  )}
                  <li className="pt-2 text-sm text-gray-500">
                    Total : {(booking.nbrAdulte ?? 0) + (booking.nbrEnfant ?? 0) + (booking.nbrBebe ?? 0)} voyageur
                    {((booking.nbrAdulte ?? 0) + (booking.nbrEnfant ?? 0) + (booking.nbrBebe ?? 0)) !== 1 ? 's' : ''}
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <Bed className="w-4 h-4" />
                  Chambres
                </h3>
                <p className="text-gray-900">{booking.nbrChambre ?? 0} chambre{(booking.nbrChambre ?? 0) !== 1 ? 's' : ''}</p>
                {booking.chambres && Array.isArray(booking.chambres) && booking.chambres.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-gray-600">
                    {booking.chambres.map((ch: { type?: string }, idx: number) => (
                      <li key={idx}>Chambre {idx + 1} : {ch.type || '—'}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {booking.personnes && Array.isArray(booking.personnes) && booking.personnes.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Liste des voyageurs</h3>
                <ul className="space-y-1 text-sm text-gray-900">
                  {booking.personnes.map((p: { type?: string; nom?: string; prenom?: string; chambre?: number }, i: number) => (
                    <li key={i}>
                      {p.type === 'adulte' ? 'Adulte' : 'Enfant'} {i + 1} : {p.nom || ''} {p.prenom || ''}
                      {p.chambre != null ? ` (chambre ${p.chambre})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {booking.bebes && Array.isArray(booking.bebes) && booking.bebes.length > 0 && (
              <div className="mt-2">
                <h3 className="text-sm font-medium text-gray-700 mb-1">Bébés</h3>
                <ul className="space-y-1 text-sm text-gray-900">
                  {booking.bebes.map((b: { nom?: string; prenom?: string }, i: number) => (
                    <li key={i}>Bébé {i + 1} : {b.nom || ''} {b.prenom || ''}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Paiement */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Paiement</h2>
            </div>
            <dl className="space-y-3">
              <div className="flex justify-between items-center">
                <dt className="text-sm text-gray-500">Montant total</dt>
                <dd className="text-lg font-semibold text-gray-900">
                  {booking.totalPrice != null
                    ? `${Number(booking.totalPrice).toLocaleString('fr-FR')} DA`
                    : '—'}
                </dd>
              </div>
              {(booking.montantAvance ?? 0) > 0 && (
                <div className="flex justify-between items-center">
                  <dt className="text-sm text-gray-500">Acompte</dt>
                  <dd className="text-gray-900">
                    {Number(booking.montantAvance).toLocaleString('fr-FR')} DA
                  </dd>
                </div>
              )}
              {(booking.montantPaye ?? 0) > 0 && (
                <div className="flex justify-between items-center">
                  <dt className="text-sm text-gray-500">Déjà payé</dt>
                  <dd className="text-emerald-600 font-medium">
                    {Number(booking.montantPaye).toLocaleString('fr-FR')} DA
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Dates & options */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Dates</h2>
            </div>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Date de création</dt>
                <dd className="text-gray-900">{formatBookingDate(booking.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Date de mise à jour</dt>
                <dd className="text-gray-900">{formatBookingDate(booking.updatedAt)}</dd>
              </div>
              {booking.delaiOption && (
                <div>
                  <dt className="text-sm text-gray-500">Délai option</dt>
                  <dd className="text-gray-900">{booking.delaiOption}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Demandes spéciales */}
        {(booking.specialRequests && booking.specialRequests.trim()) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              <h2 className="font-semibold text-gray-900">Demandes spéciales</h2>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{booking.specialRequests}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(booking.package?.id ? `/admin/packages/${booking.package.id}/clients` : '/admin/bookings')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Retour à la liste
          </button>
          <button
            type="button"
            onClick={() => navigate(`/admin/bookings/edit/${booking.id}`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Pencil className="w-4 h-4" />
            Modifier la réservation
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
